import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, FileArchive, FileSpreadsheet, FileText, File } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { TIPOS_EXPORTACAO } from '@/lib/utils/helpers';
import { useHeader } from '@/contexts';
import {
  exibeSatNoPeriodo,
  modeloDisponivel,
  opcoesDeModelo,
  useDownloadEmLoteNfse,
  useExportacaoDocumentosTurbo,
  type ServicosDownloadNfseLote,
  type TipoArquivoDownloadNfseLote,
  type TipoArquivoExportacao,
} from './documentos-helpers';
import { useToast } from '@/hooks/use-toast';

type TipoDocumentoExportacao = 'relatorios' | 'download-lote-nfse';

const TIPOS_DOCUMENTO_EXPORTACAO = [
  { value: 'relatorios', label: 'Relatórios' },
  { value: 'download-lote-nfse', label: 'XML / PDF das NFS-e em lote' },
] as const;

const SERVICOS_DOWNLOAD_NFSE_LOTE: { value: ServicosDownloadNfseLote; label: string }[] = [
  { value: '0', label: 'Tomados' },
  { value: '1', label: 'Prestados' },
  { value: 'ambos', label: 'Ambos' },
];

/** As planilhas e o PDF de sempre, mais o XML compactado das notas (SPEC-018, §3, item 6). */
const TIPOS_EXPORTACAO_DAS_NOTAS = [
  ...TIPOS_EXPORTACAO,
  { value: 'xml', label: 'XML compactado', extension: '.zip', icon: 'FileArchive' },
] as const;

const MODELO_NFSE_EXPORTACAO = '13';

const TIPOS_DOWNLOAD_NFSE_LOTE = [
  { value: 'xml', label: 'XML', extension: '.zip', icon: 'FileArchive' },
  { value: 'pdf', label: 'PDF', extension: '.zip', icon: 'FileText' },
] as const;

interface ExportarModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipoDocumento: '0' | '1';
  modeloDocumentoDefault?: string;
  habilitarDownloadLoteNfse?: boolean;
}

export function ExportarModal({
  open,
  onOpenChange,
  tipoDocumento,
  modeloDocumentoDefault,
  habilitarDownloadLoteNfse = false,
}: ExportarModalProps) {
  const [tipoExportacao, setTipoExportacao] = useState<TipoDocumentoExportacao>('relatorios');
  const [tipoArquivo, setTipoArquivo] = useState<TipoArquivoExportacao>('');
  const [tipoArquivoLoteNfse, setTipoArquivoLoteNfse] = useState<TipoArquivoDownloadNfseLote>('');
  const [modeloSelecionado, setModeloSelecionado] = useState<string>(modeloDocumentoDefault || '');
  const [entradaSaida, setEntradaSaida] = useState<'0' | '1'>(tipoDocumento);
  const [servicosLoteNfse, setServicosLoteNfse] = useState<ServicosDownloadNfseLote>(tipoDocumento);
  const { filtros: filtrosHeader } = useHeader();
  const [dataEmissaoDe, setDataEmissaoDe] = useState<string>(filtrosHeader.dataInicio || '');
  const [dataEmissaoAte, setDataEmissaoAte] = useState<string>(filtrosHeader.dataFim || '');
  // O SAT só em período até 2025, como a lista.
  const exibirSatNoPeriodo = exibeSatNoPeriodo(dataEmissaoAte);

  // O modelo que vale é o escolhido enquanto ele estiver entre as opções (o sentido e o "Até" decidem: o SAT só até
  // 2025). Fora delas, nenhum: o seletor mostra "Selecione o modelo" e a exportação não sai com um modelo que não
  // aparece. A escolha não se apaga: volta quando o período torna a valer, como num "Até" vazio no meio da digitação.
  const modeloEfetivo = modeloDocumentoDefault
    ? modeloSelecionado
    : modeloDisponivel(modeloSelecionado, {
        tipoDocumento: entradaSaida,
        incluirNfse: habilitarDownloadLoteNfse,
        exibirSat: exibirSatNoPeriodo,
      });

  useEffect(() => {
    if (tipoArquivo === 'xml' && modeloEfetivo === MODELO_NFSE_EXPORTACAO) {
      setTipoArquivo('');
    }
  }, [modeloEfetivo, tipoArquivo]);
  const { toast } = useToast();
  const cnpjContribuinteDownloadLoteNfse = filtrosHeader.empresaCnpjs?.trim() || undefined;

  const exportacao = useExportacaoDocumentosTurbo({
    dataEmissaoDe,
    dataEmissaoAte,
    modeloDocumento: modeloEfetivo,
    entradaSaida,
    tipoArquivo,
    cnpjContribuinte: filtrosHeader.empresaCnpjs || undefined,
  });

  const downloadLoteNfse = useDownloadEmLoteNfse({
    dataEmissaoDe,
    dataEmissaoAte,
    tipoArquivo: tipoArquivoLoteNfse,
    servicos: servicosLoteNfse,
    cnpjContribuinte: cnpjContribuinteDownloadLoteNfse,
  });

  const resetForm = useCallback(() => {
    setTipoExportacao('relatorios');
    setTipoArquivo('');
    setTipoArquivoLoteNfse('');
    setModeloSelecionado(modeloDocumentoDefault || '');
    setEntradaSaida(tipoDocumento);
    setServicosLoteNfse(tipoDocumento);
    setDataEmissaoDe(filtrosHeader.dataInicio || '');
    setDataEmissaoAte(filtrosHeader.dataFim || '');
  }, [filtrosHeader.dataFim, filtrosHeader.dataInicio, modeloDocumentoDefault, tipoDocumento]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  // A janela reaberta começa sem a falha da vez anterior.
  const { limparErro: limparErroDaExportacao } = exportacao;
  useEffect(() => {
    if (open) {
      limparErroDaExportacao();
    }
  }, [open, limparErroDaExportacao]);

  const handleExportar = async () => {
    const exportador = tipoExportacao === 'download-lote-nfse' ? downloadLoteNfse : exportacao;

    if (exportador.isPending) return;

    toast({
      title:
        tipoExportacao === 'download-lote-nfse'
          ? 'Processando download em lote'
          : 'Processando exportação',
      description:
        tipoExportacao === 'download-lote-nfse'
          ? 'As NFS-e estão sendo preparadas para download.'
          : 'O documento está sendo processado e preparado para download.',
    });

    const ok = await exportador.exportar();
    if (!ok) return;
    onOpenChange(false);
    resetForm();
  };

  const handleFechar = () => {
    onOpenChange(false);
    resetForm();
  };

  const isDownloadLoteNfse = tipoExportacao === 'download-lote-nfse';
  const exportadorAtual = isDownloadLoteNfse ? downloadLoteNfse : exportacao;
  const isFormValid = exportadorAtual.isFormValid;
  const isPeriodoValido = exportadorAtual.isPeriodoValido;
  // O XML compactado é das notas: a NFS-e (13) tem o próprio, no download em lote (SPEC-018 §6).
  const opcoesFormato = isDownloadLoteNfse
    ? TIPOS_DOWNLOAD_NFSE_LOTE
    : modeloEfetivo === MODELO_NFSE_EXPORTACAO
      ? TIPOS_EXPORTACAO
      : TIPOS_EXPORTACAO_DAS_NOTAS;
  const gridFormatosClassName =
    opcoesFormato.length === 2
      ? 'grid-cols-2'
      : opcoesFormato.length === 3
        ? 'grid-cols-3'
        : 'grid-cols-4';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-8">
        <DialogHeader>
          <DialogTitle>Exportar Documentos</DialogTitle>
          <DialogDescription>
            {habilitarDownloadLoteNfse
              ? 'Selecione o tipo de documento e o formato para download'
              : modeloDocumentoDefault
                ? 'Selecione o formato de exportação'
                : 'Selecione o modelo de documento e o formato de exportação'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {habilitarDownloadLoteNfse && (
            <div className="flex flex-col gap-2">
              <Label className="text-base font-medium">
                Tipo de documento <span className="text-red-500">*</span>
              </Label>
              <Select
                value={tipoExportacao}
                onValueChange={value => {
                  const novoTipoExportacao = value as TipoDocumentoExportacao;

                  setTipoExportacao(novoTipoExportacao);
                  setTipoArquivo('');
                  setTipoArquivoLoteNfse(novoTipoExportacao === 'download-lote-nfse' ? 'xml' : '');
                }}
              >
                <SelectTrigger className="w-full h-11">
                  <SelectValue placeholder="Selecione o tipo de documento" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO_EXPORTACAO.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!modeloDocumentoDefault && (
            <div className="flex flex-col gap-2">
              <Label className="text-base font-medium">
                Modelo do Documento <span className="text-red-500">*</span>
              </Label>
              <Select value={modeloEfetivo} onValueChange={setModeloSelecionado}>
                <SelectTrigger className="w-full h-11">
                  <SelectValue placeholder="Selecione o modelo" />
                </SelectTrigger>
                <SelectContent>
                  {opcoesDeModelo({
                    tipoDocumento: entradaSaida,
                    incluirNfse: habilitarDownloadLoteNfse,
                    exibirSat: exibirSatNoPeriodo,
                  }).map(modelo => (
                    <SelectItem key={modelo.value} value={modelo.value}>
                      {modelo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isDownloadLoteNfse && (
            <div className="flex flex-col gap-3">
              <Label className="text-base font-medium">
                Serviços <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-3 gap-3">
                {SERVICOS_DOWNLOAD_NFSE_LOTE.map(opcao => (
                  <Button
                    key={opcao.value}
                    type="button"
                    variant={servicosLoteNfse === opcao.value ? 'default' : 'outline'}
                    onClick={() => setServicosLoteNfse(opcao.value)}
                  >
                    {opcao.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {!isDownloadLoteNfse && (
            <div className="flex flex-col gap-3">
              <Label className="text-base font-medium">
                Entrada / Saída <span className="text-red-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant={entradaSaida === '0' ? 'default' : 'outline'}
                  onClick={() => setEntradaSaida('0')}
                >
                  Entrada
                </Button>
                <Button
                  type="button"
                  variant={entradaSaida === '1' ? 'default' : 'outline'}
                  onClick={() => setEntradaSaida('1')}
                >
                  Saída
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Label className="text-base font-medium">
              Período de Emissão <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label className="text-sm text-muted-foreground">De</Label>
                <Input
                  type="date"
                  value={dataEmissaoDe}
                  onChange={e => setDataEmissaoDe(e.target.value)}
                  aria-invalid={!!dataEmissaoDe && !!dataEmissaoAte && !isPeriodoValido}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm text-muted-foreground">Até</Label>
                <Input
                  type="date"
                  value={dataEmissaoAte}
                  onChange={e => setDataEmissaoAte(e.target.value)}
                  aria-invalid={!!dataEmissaoDe && !!dataEmissaoAte && !isPeriodoValido}
                />
              </div>
            </div>
            {!isPeriodoValido && (
              <p className="text-xs text-destructive">O período informado é inválido.</p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <Label className="text-base font-medium">
              {isDownloadLoteNfse ? 'Formato do Download' : 'Formato de Exportação'}{' '}
              <span className="text-red-500">*</span>
            </Label>
            <div className={`grid ${gridFormatosClassName} gap-3`}>
              {opcoesFormato.map(
                (tipo: { value: string; label: string; extension: string; icon: string }) => {
                  const Icon =
                    tipo.icon === 'FileSpreadsheet'
                      ? FileSpreadsheet
                      : tipo.icon === 'FileText'
                        ? FileText
                        : tipo.icon === 'FileArchive'
                          ? FileArchive
                          : File;
                  const isSelected = isDownloadLoteNfse
                    ? tipoArquivoLoteNfse === tipo.value
                    : tipoArquivo === tipo.value;

                  return (
                    <button
                      key={tipo.value}
                      type="button"
                      onClick={() => {
                        if (isDownloadLoteNfse) {
                          setTipoArquivoLoteNfse(tipo.value as TipoArquivoDownloadNfseLote);
                          return;
                        }

                        setTipoArquivo(tipo.value as TipoArquivoExportacao);
                      }}
                      className={`flex flex-col items-center justify-center gap-3 p-6 rounded-lg border-2 transition-all hover:bg-accent/50 ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-border bg-background'
                      }`}
                    >
                      <Icon
                        className={`h-8 w-8 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                      <div className="text-center">
                        <p
                          className={`font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}
                        >
                          {tipo.label}
                        </p>
                        <p className="text-xs text-muted-foreground">{tipo.extension}</p>
                      </div>
                    </button>
                  );
                }
              )}
            </div>
          </div>

          {(!isFormValid || !isPeriodoValido) && (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <span className="font-medium">Atenção:</span>{' '}
                {modeloDocumentoDefault
                  ? isDownloadLoteNfse
                    ? 'Selecione o formato, a empresa e o período para continuar.'
                    : 'Selecione o formato de exportação, o tipo (Entrada/Saída) e o período para continuar.'
                  : 'Selecione o modelo do documento e o formato de exportação, o tipo (Entrada/Saída) e o período para continuar.'}
              </p>
            </div>
          )}

          {/* A falha fica na janela: o toast some em 5 s, e a janela continua aberta (sustentação de 26/10, item 4). */}
          {!isDownloadLoteNfse && exportacao.erro && (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>Não foi possível exportar</AlertTitle>
              <AlertDescription>{exportacao.erro}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={handleFechar}>
            Cancelar
          </Button>
          <Button
            onClick={handleExportar}
            disabled={!isFormValid || !isPeriodoValido || exportadorAtual.isPending}
          >
            {exportadorAtual.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner />
                Exportando...
              </span>
            ) : (
              'Exportar'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
